import React, { memo, useState } from "react";
import { CheckboxNew, IconCheckCircle } from "../../../../assests/icons/Icons";
import { useAppSelector } from "../../../../ReduxTool/store/hooks";
import { SELF_MODE } from "../../../../Utils/Const";

/* ─── Terms of Service content ───────────────────────────── */

const termsContent = `These Terms & Conditions ("Terms") govern your use of Terranxt's solar proposal, installation services, and related support. By approving the proposal or proceeding with payment, you agree to these Terms.

1. Scope of Service
Terranxt provides end-to-end coordination for residential solar installation, including:
  · System design and energy estimation
  · Installer assignment and project management
  · Documentation, approvals, and commissioning support
  · Customer communication and milestone tracking
Terranxt acts as a service facilitator and project manager, working with verified third-party installers and vendors.

2. Proposal Acceptance
By approving the proposal, you confirm that:
  · The system size, pricing, and estimated savings have been reviewed by you.
  · You understand that energy generation and savings are estimates, not guarantees.
  · Minor technical adjustments may occur during detailed engineering or installation.
  · Any major cost or scope change will require your approval before proceeding.

3. Pricing & Payments

3.1 Transparent Pricing
The project cost includes components, installation, and applicable taxes unless stated otherwise. Government subsidies or incentives are shown only if eligibility criteria are met.

3.2 Milestone-Based Payments
Payments are collected in clearly defined stages such as:
  · Booking / confirmation
  · Material dispatch or installation start
  · Installation completion
  · Final commissioning
You will always be informed before any payment is due.

3.3 No Hidden Charges
No additional charges will be applied without your explicit consent.

4. Installation & Timeline
Typical project completion time is estimated, not guaranteed. Delays may occur due to:
  · Weather conditions
  · Government approvals or utility processes
  · Site readiness or structural limitations
  · Force-majeure events beyond reasonable control
Terranxt will keep you regularly informed of progress and delays.

5. Performance & Savings Disclaimer
Energy production estimates are generated using industry-standard simulation tools and site data. Actual performance may vary based on:
  · Weather and sunlight conditions
  · Roof orientation or shading changes
  · Household energy usage
  · Grid availability or outages
Terranxt does not guarantee specific financial savings or payback duration.

6. Installer Responsibility & Warranty
Installation is performed by qualified third-party installers. Equipment warranties are provided by original manufacturers. Workmanship warranty terms are provided by the assigned installer. Terranxt will assist in coordination and support, but warranty claims follow manufacturer/installer policies.

7. Customer Responsibilities
You agree to:
  · Provide accurate site and contact information.
  · Ensure safe and timely access to the installation location.
  · Complete required documentation, approvals, or payments when requested.
Failure to meet these may result in project delays or rescheduling.

8. Cancellation & Refund Policy
Before Installation Starts
Cancellation may be allowed with applicable administrative or site-survey charges deducted.
After Material Dispatch or Installation Start
Refunds may be partial or not applicable, depending on incurred costs. Exact refund details will be shared at the time of cancellation request.

9. Limitation of Liability
To the maximum extent permitted by law:
Terranxt is not liable for indirect, incidental, or consequential losses, including loss of savings or revenue. Total liability, if any, will not exceed the amount paid to Terranxt for services. Nothing in these Terms limits rights that cannot be excluded under applicable law.

10. Privacy & Data Use
Your information may be used to:
  · Design your solar system
  · Coordinate installation and approvals
  · Provide updates, invoices, and support
Data is handled according to Terranxt's Privacy Policy and is not sold without consent, except where required for service delivery.

11. Governing Law
These Terms are governed by the applicable local laws of the project location. Any disputes will be subject to the jurisdiction of the competent local courts.

12. Updates to Terms
Terranxt may update these Terms to reflect:
  · Regulatory changes
  · Service improvements
  · Legal requirements
The latest version will always be available for review before payment or agreement.`;

/* ─── Agreement summary items ─────────────────────────────── */

const summaryGroups = [
  {
    heading: "System & Pricing",
    items: [
      "All pricing shown includes applicable taxes, subsidies (if eligible), and installation services unless stated otherwise.",
      "You have reviewed and accepted the final system size, total project cost, and estimated savings.",
    ],
  },
  {
    heading: "Performance & Estimates",
    items: [
      "Energy generation and savings estimates are based on advanced simulation tools and site-specific analysis.",
      "Actual performance may vary slightly due to weather, usage patterns, or site conditions.",
    ],
  },
  {
    heading: "End-to-End Installation",
    items: [
      "Your solar installation will be fully managed from design and approvals to commissioning and activation.",
      "A qualified, vetted installer will be assigned based on quality, location, and best available price.",
    ],
  },
  {
    heading: "Project Tracking & Support",
    items: [
      "You will receive regular project updates and status tracking throughout installation.",
      "Dedicated customer support will be available until your system is fully operational.",
    ],
  },
  {
    heading: "Payments & Milestones",
    items: [
      "Payments will follow secure, clearly defined project milestones.",
      "No hidden charges will be added without your prior approval.",
    ],
  },
];

const selfModeInstallationGroup = {
  heading: "Self-Managed Installation",
  items: [
    "Your request is shared with verified installers who will contact you with proposals.",
    "You compare, choose, and coordinate installation directly with your selected installer.",
  ],
};


/* ─── Contract component ──────────────────────────────────── */

interface ContractProps {
  agreed: boolean;
  setAgreed: React.Dispatch<React.SetStateAction<boolean>>;
  checkboxRef?: React.RefObject<HTMLDivElement>;
  checkboxError: boolean;
  setCheckboxError: React.Dispatch<React.SetStateAction<boolean>>;
}

const Contract = ({ agreed, setAgreed, checkboxRef, checkboxError, setCheckboxError }: ContractProps) => {
  const [tosExpanded, setTosExpanded] = useState(true);
  const { roofAnalysis: { selected_project } } = useAppSelector(state => state.consumerReducers);
  const isSelfMode = selected_project?.installationmode === SELF_MODE;
  const groups = isSelfMode
    ? summaryGroups.map(g => g.heading === "End-to-End Installation" ? selfModeInstallationGroup : g)
    : summaryGroups;

  const handleAgree = () => {
    setAgreed((p) => !p);
    setCheckboxError(false);
  }

  return (
    <div className="grid grid-cols-[40%_60%] gap-3 p-2 bg-white min-w-[800px]">

      {/* ── Left: Agreement Summary ── */}
      <div className="border border-gray-200 rounded-md p-6 overflow-y-auto custom-scrollbar-css max-h-[55vh]">
        <h2 className="heading-20-inter-info mb-1">
          Agreement Summary
        </h2>
        <p className="heading-12-inter-normal mb-5 pb-4 border-b border-gray-200">
          {isSelfMode
            ? "Connect with verified installers and manage your solar installation directly."
            : "Hassle-free solar installation managed end-to-end for your home."}
        </p>


        <p className="heading-16-inter-600 text-[14px] mb-5">
          By approving this plan, you confirm:
        </p>

        <div className="flex flex-col gap-5">
          {groups.map((group) => (
            <div key={group.heading}>
              <p className="heading-12-inter mb-2.5">
                {group.heading}
              </p>
              <div className="flex flex-col gap-2.5">
                {group.items.map((item) => (
                  <div key={item} className="flex items-start gap-2">
                    <IconCheckCircle />
                    <span className="heading-12-inter-normal -mt-1">
                      {item}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Right: Terms of Service ── */}
      <div className="border border-gray-200 rounded-md overflow-hidden flex flex-col max-h-[55vh] mr-1">

        {/* TOS header / toggle */}
        <button
          onClick={() => setTosExpanded((p) => !p)}
          className="flex items-center justify-between w-full px-4 py-3 bg-white border-b border-gray-200 text-left"
        >
          <span className="text-10-roboto uppercase tracking-widest text-[#616161]">
            Terms of Service
          </span>
          <svg
            width="16" height="16" viewBox="0 0 16 16" fill="none"
            className={`transition-transform duration-200 ${tosExpanded ? "" : "rotate-180"}`}
          >
            <path d="M4 6L8 10L12 6" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        {/* Scrollable TOS body */}
        {tosExpanded && (
          <div className="flex-1 overflow-y-auto custom-scrollbar-css px-4 py-4">
            {/* Bold intro line + date */}
            <div className="flex justify-between items-start mb-3">
              <p className="heading-12-roboto max-w-[60%]">
                This agreement explains your solar installation, pricing, and responsibilities.
              </p>
              <span className="heading-12-inter whitespace-nowrap ml-4 mt-0.5">
                Last updated: [12-02-2026]
              </span>
            </div>

            {/* Full TOS body */}
            <pre
              className="text-12-roboto leading-relaxed whitespace-pre-wrap "
            >
              {termsContent}
            </pre>


          </div>
        )}
        <div className=" p-3 pt-1 ">
          {/* Agree checkbox */}
          <div className="flex items-center gap-2  ">
            <button onClick={handleAgree} className={["w-5 h-5 rounded flex items-center justify-center flex-shrink-0 border transition-colors",
              checkboxError ? " text-red-800 border-red-800 " : agreed ? "bg-gray-900 border-gray-900" : "bg-white border-gray-400",].join(" ")} >
              {agreed && (<CheckboxNew />)}
            </button>
            <span className={` heading-14-inter-normal ${checkboxError && " !text-red-800 "} `}>
              By proceeding, you confirm that you have read, understood, and agreed to these{" "}
              <span className={` heading-14-inter-normal ${checkboxError && " !text-red-800 "}  underline cursor-pointer `}>
                Terms &amp; Conditions
              </span>
            </span>
          </div>

          {/* Note */}
          <p className="heading-12-inter-normal mt-3">
            <span className="font-medium">Note:</span> No payment is charged at this step.
            Payment is requested only after agreement confirmation and milestone initiation.
          </p>
        </div>
      </div>
    </div>
  );
};

export default memo(Contract);